import React, { useState, useEffect } from 'react';
import { initializeMatomo, optOutMatomo } from '../utils/matomo';

const CookieConsent = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState({
    essential: true, // Always required, cannot be disabled
    analytics: true,
    functional: true,
  });

  useEffect(() => {
    // Check if user has already made a cookie choice
    const cookieConsent = localStorage.getItem('cookieConsent');
    if (!cookieConsent) {
      // Small delay to prevent flash on page load
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
    // Note: Matomo initialization for returning users is handled in App.jsx
  }, []);

  const handlePreferenceChange = (key) => {
    if (key === 'essential') return; // Cannot disable essential cookies
    setPreferences(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const acceptAll = () => {
    const allAccepted = {
      essential: true,
      analytics: true,
      functional: true,
    };
    savePreferences(allAccepted, 'accepted');
  };

  const declineAll = () => {
    const allDeclined = {
      essential: true, // Essential always on
      analytics: false,
      functional: false,
    };
    savePreferences(allDeclined, 'declined');
  };

  const saveCustomPreferences = () => {
    savePreferences(preferences, 'custom');
  };

  const savePreferences = (prefs, consentType) => {
    localStorage.setItem('cookieConsent', consentType);
    localStorage.setItem('cookieConsentDate', new Date().toISOString());
    localStorage.setItem('cookiePreferences', JSON.stringify(prefs));
    
    // Handle Matomo based on analytics preference
    if (prefs.analytics) {
      // User consented to analytics - initialize Matomo now
      initializeMatomo();
    } else {
      // User declined analytics - opt them out
      optOutMatomo();
    }
    
    setIsVisible(false);
    setShowPreferences(false);
  };

  if (!isVisible) return null;

  return (
    <div className="cookie-consent-overlay">
      <div className={`cookie-consent-container ${showPreferences ? 'cookie-consent-expanded' : ''}`}>
        {!showPreferences ? (
          // Main Banner View
          <>
            <div className="cookie-consent-content">
              <div className="cookie-consent-icon">
                <span className="material-icons">cookie</span>
              </div>
              <div className="cookie-consent-text">
                <h3 className="cookie-consent-title">Cookie Notice</h3>
                <p className="cookie-consent-description">
                  We use cookies and similar technologies to enhance your browsing experience 
                  and analyze site traffic. You can accept all cookies, decline non-essential 
                  cookies, or customize your preferences.
                </p>
                <a href="/privacy-policy" className="cookie-consent-link">
                  Learn more about our Privacy Policy
                </a>
              </div>
            </div>
            <div className="cookie-consent-buttons">
              <button 
                onClick={() => setShowPreferences(true)} 
                className="cookie-btn-customize"
              >
                Customize
              </button>
              <button 
                onClick={declineAll} 
                className="cookie-btn-decline"
              >
                Decline All
              </button>
              <button 
                onClick={acceptAll} 
                className="cookie-btn-accept"
              >
                Accept All
              </button>
            </div>
          </>
        ) : (
          // Preferences View
          <>
            <div className="cookie-preferences-header">
              <button 
                onClick={() => setShowPreferences(false)} 
                className="cookie-back-btn"
                aria-label="Go back"
              >
                <span className="material-icons">arrow_back</span>
              </button>
              <h3 className="cookie-consent-title">Cookie Preferences</h3>
            </div>
            
            <p className="cookie-preferences-description">
              Manage your cookie preferences below. Essential cookies are required for the site to function 
              and cannot be disabled.
            </p>

            <div className="cookie-preferences-list">
              {/* Essential Cookies */}
              <div className="cookie-preference-item">
                <div className="cookie-preference-info">
                  <div className="cookie-preference-header">
                    <span className="material-icons cookie-preference-icon">lock</span>
                    <h4>Essential Cookies</h4>
                    <span className="cookie-required-badge">Required</span>
                  </div>
                  <p>Required for the website to function properly. These cannot be disabled.</p>
                </div>
                <label className="cookie-toggle cookie-toggle-disabled">
                  <input 
                    type="checkbox" 
                    checked={true} 
                    disabled 
                  />
                  <span className="cookie-toggle-slider"></span>
                </label>
              </div>

              {/* Analytics Cookies */}
              <div className="cookie-preference-item">
                <div className="cookie-preference-info">
                  <div className="cookie-preference-header">
                    <span className="material-icons cookie-preference-icon">analytics</span>
                    <h4>Analytics Cookies</h4>
                  </div>
                  <p>Help us understand how visitors interact with our website by collecting anonymous usage data.</p>
                </div>
                <label className="cookie-toggle">
                  <input 
                    type="checkbox" 
                    checked={preferences.analytics} 
                    onChange={() => handlePreferenceChange('analytics')}
                  />
                  <span className="cookie-toggle-slider"></span>
                </label>
              </div>

              {/* Functional Cookies */}
              <div className="cookie-preference-item">
                <div className="cookie-preference-info">
                  <div className="cookie-preference-header">
                    <span className="material-icons cookie-preference-icon">tune</span>
                    <h4>Functional Cookies</h4>
                  </div>
                  <p>Remember your preferences and settings to provide enhanced functionality and personalization.</p>
                </div>
                <label className="cookie-toggle">
                  <input 
                    type="checkbox" 
                    checked={preferences.functional} 
                    onChange={() => handlePreferenceChange('functional')}
                  />
                  <span className="cookie-toggle-slider"></span>
                </label>
              </div>
            </div>

            <div className="cookie-consent-buttons cookie-preferences-buttons">
              <button 
                onClick={declineAll} 
                className="cookie-btn-decline"
              >
                Decline All
              </button>
              <button 
                onClick={saveCustomPreferences} 
                className="cookie-btn-accept"
              >
                Save Preferences
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CookieConsent;
